import { useState, useEffect } from "react";
import { cmToInput, inputToCm, lengthLabel } from "@/lib/units";

/**
 * MeasurementsBlock — renders a category's measurement_schema as typed inputs.
 *
 * Storage contract:
 *  - length fields are persisted as cm floats in `measurements_map[key]`
 *  - free fields are persisted as raw strings in `measurements_map[key]`
 *
 * Display contract:
 *  - length fields are rendered in the studio's current unit_system (cm vs in)
 *  - the unit label is shown to the right of each length input
 *  - length inputs buffer their raw string locally and commit (with cm conversion)
 *    on blur, so typing "3.567 in" doesn't get progressively rounded on re-render.
 *
 * Props:
 *  - schema: [{key, label, unit_kind}]
 *  - values: object of {key -> value} (current measurements_map)
 *  - unitSystem: "metric" | "imperial"
 *  - onChange(nextMap): emits the full next map
 *  - fallbackValue, onFallbackChange: free-text controls used when no schema
 */
export default function MeasurementsBlock({ schema, values, unitSystem, onChange, fallbackValue, onFallbackChange }) {
  const list = schema || [];
  const map = values || {};

  if (list.length === 0) {
    return (
      <div className="md:col-span-2" data-testid="measurements-fallback">
        <label className="editorial-label">measurements ({lengthLabel(unitSystem)})</label>
        <input
          type="text"
          value={fallbackValue ?? ""}
          onChange={(e) => onFallbackChange(e.target.value)}
          data-testid="field-measurements"
          className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal"
        />
      </div>
    );
  }

  return (
    <div className="md:col-span-2" data-testid="measurements-typed">
      <label className="editorial-label">measurements · per category schema</label>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4 mt-2">
        {list.map((f) => {
          if (f.unit_kind === "length") {
            return (
              <LengthInput
                key={f.key}
                field={f}
                cmValue={map[f.key]}
                unitSystem={unitSystem}
                onCommit={(cm) => onChange({ ...map, [f.key]: cm })}
              />
            );
          }
          return (
            <div key={f.key}>
              <label className="text-[0.65rem] tracking-widest uppercase text-taupe">{f.label}</label>
              <input
                type="text"
                value={map[f.key] ?? ""}
                onChange={(e) => onChange({ ...map, [f.key]: e.target.value })}
                onFocus={(e) => e.target.select()}
                data-testid={`measurement-${f.key}`}
                className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none text-charcoal"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LengthInput({ field, cmValue, unitSystem, onCommit }) {
  // Local buffer keeps the raw typed string verbatim until blur, avoiding lossy
  // cm↔in round-trips on each keystroke. We re-sync from the canonical cm value
  // whenever the parent or unit system changes.
  const display = cmToInput(cmValue, unitSystem);
  const [raw, setRaw] = useState(display === "" ? "" : String(display));

  useEffect(() => {
    const next = cmToInput(cmValue, unitSystem);
    setRaw(next === "" ? "" : String(next));
  }, [cmValue, unitSystem]);

  const handleBlur = () => {
    const cm = raw === "" ? 0 : inputToCm(raw, unitSystem);
    if (cm !== cmValue) onCommit(cm);
  };

  return (
    <div>
      <label className="text-[0.65rem] tracking-widest uppercase text-taupe">{field.label}</label>
      <div className="flex items-center gap-2 border-b border-taupe py-1 mt-1">
        <input
          type="number"
          step="0.01"
          min="0"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onBlur={handleBlur}
          onFocus={(e) => e.target.select()}
          data-testid={`measurement-${field.key}`}
          className="flex-1 bg-transparent focus:outline-none text-charcoal"
        />
        <span className="text-[0.6rem] tracking-widest uppercase text-taupe">{lengthLabel(unitSystem)}</span>
      </div>
    </div>
  );
}
